// Floor view rendering. SVG + D3 zoom per spec §4.2.
//
// Layout strategy (simple, deterministic):
//   - Entities laid out in a grid (auto cols based on count)
//   - Within each entity, categories stack vertically
//   - Subcategories indent inside their parent category
//   - Tables render as octagons inside their lowest-level container,
//     flowing left-to-right and wrapping
//
// We compute layout in pure JS, then render as SVG. D3 only handles zoom/pan.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api.js';
import { select } from 'd3-selection';
import { zoom, zoomIdentity, type ZoomBehavior } from 'd3-zoom';
import type {
  FloorResponse,
  FloorEntity,
  FloorCategory,
  FloorSubcategory,
  FloorTable,
  StatusFlag,
} from '@command-floor/shared-types';

const ENTITY_COLS = 2;
const ENTITY_W = 720;
const ENTITY_H = 480;
const ENTITY_GAP = 48;
const ENTITY_PADDING_TOP = 64;
const ENTITY_PADDING_X = 24;
const ENTITY_PADDING_BOTTOM = 24;

const CATEGORY_GAP = 16;
const CATEGORY_HEADER_H = 32;
const CATEGORY_PADDING = 22;

const TABLE_RADIUS = 28;
const TABLE_GAP = 16;
const TABLE_LABEL_OFFSET = TABLE_RADIUS + 14;
const TABLE_LABEL_LINE_H = 13;

// Pixels reserved at the top of the SVG viewport for the branding logo overlay
const LOGO_H = 77;
const LOGO_CLEAR = LOGO_H + 40; // logo height + breathing room

interface LaidOutTable {
  table: FloorTable;
  cx: number;
  cy: number;
}

interface LaidOutCategory {
  category: FloorCategory | FloorSubcategory;
  x: number;
  y: number;
  w: number;
  h: number;
  isSub: boolean;
  tables: LaidOutTable[];
}

interface LaidOutEntity {
  entity: FloorEntity;
  x: number;
  y: number;
  w: number;
  h: number;
  categories: LaidOutCategory[];
}

interface Layout {
  width: number;
  height: number;
  entities: LaidOutEntity[];
}

function octagonPoints(r: number): string {
  // 8 vertices offset by π/8 so the octagon has flat top/bottom (stop-sign style)
  return Array.from({ length: 8 }, (_, i) => {
    const a = (i * Math.PI) / 4 + Math.PI / 8;
    return `${(r * Math.cos(a)).toFixed(2)},${(r * Math.sin(a)).toFixed(2)}`;
  }).join(' ');
}

function flagFill(status: StatusFlag): string {
  switch (status) {
    case 'GREY':
      return '#E5E7EB';
    case 'YELLOW':
      return '#FDE68A';
    case 'RED':
      return '#FECACA';
  }
}

const BORDER_WIDTH = 3;
const LOCKED_FILL = '#374151';
const LOCKED_STROKE = '#6B7280';

function LockIcon() {
  return (
    <g>
      {/* shackle */}
      <path
        d="M -4 -1 L -4 -5 Q 0 -9 4 -5 L 4 -1"
        stroke="#9CA3AF"
        strokeWidth="1.8"
        fill="none"
        strokeLinecap="round"
      />
      {/* body */}
      <rect x="-5.5" y="-1" width="11" height="9" rx="2" fill="#9CA3AF" />
      {/* keyhole */}
      <circle cx="0" cy="3" r="1.5" fill="#374151" />
    </g>
  );
}

function borderStroke(table: FloorTable): { color: string; width: number } {
  if (table.is_blue_for_viewer) return { color: '#2563EB', width: BORDER_WIDTH };
  switch (table.status_flag) {
    case 'RED':
      return { color: '#DC2626', width: BORDER_WIDTH };
    case 'YELLOW':
      return { color: '#D97706', width: BORDER_WIDTH };
    case 'GREY':
      return { color: '#6B7280', width: BORDER_WIDTH };
  }
}

function computeLayout(data: FloorResponse): Layout {
  const entities = data.entities;
  const cols = Math.min(ENTITY_COLS, Math.max(1, entities.length));
  const rows = Math.ceil(entities.length / cols);

  const laidOutEntities: LaidOutEntity[] = entities.map((entity, idx) => {
    const col = idx % cols;
    const row = Math.floor(idx / cols);
    const ex = col * (ENTITY_W + ENTITY_GAP);
    const ey = row * (ENTITY_H + ENTITY_GAP);

    // Lay out categories vertically inside the entity.
    const innerW = ENTITY_W - ENTITY_PADDING_X * 2;
    const usableTop = ENTITY_PADDING_TOP;
    let cursorY = usableTop;

    const laidOutCategories: LaidOutCategory[] = [];

    for (const cat of entity.categories) {
      const catX = ENTITY_PADDING_X;
      const catW = innerW;

      // Category header + own tables
      const directTablesH = laidOutTablesHeight(cat.tables, catW - CATEGORY_PADDING * 2);
      let catH = CATEGORY_HEADER_H + CATEGORY_PADDING + directTablesH + CATEGORY_PADDING;

      // Add subcategories' total height
      const subLayouts: LaidOutCategory[] = [];
      let subY = catH;
      for (const sub of cat.subcategories) {
        const subInnerW = catW - CATEGORY_PADDING * 2;
        const subTablesH = laidOutTablesHeight(sub.tables, subInnerW - CATEGORY_PADDING * 2);
        const subH = CATEGORY_HEADER_H + CATEGORY_PADDING + subTablesH + CATEGORY_PADDING;
        subLayouts.push({
          category: sub,
          x: catX + CATEGORY_PADDING,
          y: cursorY + subY,
          w: subInnerW,
          h: subH,
          isSub: true,
          tables: layOutTables(
            sub.tables,
            catX + CATEGORY_PADDING * 2,
            cursorY + subY + CATEGORY_HEADER_H + CATEGORY_PADDING,
            subInnerW - CATEGORY_PADDING * 2,
          ),
        });
        subY += subH + CATEGORY_GAP;
      }
      catH += subLayouts.length > 0 ? subY - catH + CATEGORY_GAP : 0;

      laidOutCategories.push({
        category: cat,
        x: catX,
        y: cursorY,
        w: catW,
        h: catH,
        isSub: false,
        tables: layOutTables(
          cat.tables,
          catX + CATEGORY_PADDING,
          cursorY + CATEGORY_HEADER_H + CATEGORY_PADDING,
          catW - CATEGORY_PADDING * 2,
        ),
      });

      // Push subcategory layouts as siblings so we don't recurse render
      laidOutCategories.push(...subLayouts);

      cursorY += catH + CATEGORY_GAP;
    }

    // Auto-grow entity height if needed
    const entityH = Math.max(ENTITY_H, cursorY + ENTITY_PADDING_BOTTOM);

    return {
      entity,
      x: ex,
      y: ey,
      w: ENTITY_W,
      h: entityH,
      categories: laidOutCategories,
    };
  });

  // Recompute total bounds (entity heights may have grown)
  let totalW = cols * (ENTITY_W + ENTITY_GAP) - ENTITY_GAP;
  let totalH = 0;
  for (let r = 0; r < rows; r++) {
    const rowEntities = laidOutEntities.slice(r * cols, (r + 1) * cols);
    const rowH = Math.max(...rowEntities.map((e) => e.h), ENTITY_H);
    // re-anchor entity y to row-stacked layout
    rowEntities.forEach((e) => {
      e.y = totalH;
    });
    totalH += rowH + ENTITY_GAP;
  }
  totalH = Math.max(totalH - ENTITY_GAP, ENTITY_H);

  return {
    width: totalW,
    height: totalH,
    entities: laidOutEntities,
  };
}

function laidOutTablesHeight(tables: FloorTable[], availW: number): number {
  if (tables.length === 0) return TABLE_RADIUS * 2; // empty bay still has min height
  const perRow = Math.max(1, Math.floor((availW + TABLE_GAP) / (TABLE_RADIUS * 2 + TABLE_GAP)));
  const rowCount = Math.ceil(tables.length / perRow);
  return rowCount * (TABLE_RADIUS * 2 + TABLE_GAP + 18) + TABLE_GAP;
}

function layOutTables(tables: FloorTable[], x: number, y: number, availW: number): LaidOutTable[] {
  const perRow = Math.max(1, Math.floor((availW + TABLE_GAP) / (TABLE_RADIUS * 2 + TABLE_GAP)));
  return tables.map((t, i) => {
    const col = i % perRow;
    const row = Math.floor(i / perRow);
    return {
      table: t,
      cx: x + TABLE_RADIUS + col * (TABLE_RADIUS * 2 + TABLE_GAP),
      cy: y + TABLE_RADIUS + row * (TABLE_RADIUS * 2 + TABLE_GAP + 18),
    };
  });
}

export function FloorView({ data }: { data: FloorResponse }) {
  const navigate = useNavigate();
  const svgRef = useRef<SVGSVGElement | null>(null);
  const gRef = useRef<SVGGElement | null>(null);
  const zoomBehaviorRef = useRef<ZoomBehavior<SVGSVGElement, unknown> | null>(null);
  const [lockedModal, setLockedModal] = useState<{ id: string; name: string } | null>(null);
  const [requestState, setRequestState] = useState<'idle' | 'pending' | 'sent' | 'duplicate'>(
    'idle',
  );

  const layout = useMemo(() => computeLayout(data), [data]);

  // Wire up D3 zoom
  useEffect(() => {
    if (!svgRef.current || !gRef.current) return;
    const svg = select(svgRef.current);
    const g = select(gRef.current);

    const zoomBehavior = zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.25, 4])
      .on('zoom', (event) => {
        g.attr('transform', event.transform.toString());
      });
    zoomBehaviorRef.current = zoomBehavior;

    svg.call(zoomBehavior);

    // Fit to viewport on first render, leaving LOGO_CLEAR px at the top for the branding logo
    const svgEl = svgRef.current;
    const { width: vw, height: vh } = svgEl.getBoundingClientRect();
    const availH = vh - LOGO_CLEAR;
    const scale = Math.min(vw / (layout.width + 80), availH / (layout.height + 80), 1);
    const tx = (vw - layout.width * scale) / 2;
    const ty = LOGO_CLEAR + (availH - layout.height * scale) / 2;
    svg.call(zoomBehavior.transform, zoomIdentity.translate(tx, ty).scale(scale));

    return () => {
      svg.on('.zoom', null);
    };
  }, [layout]);

  const reset = () => {
    if (!svgRef.current || !zoomBehaviorRef.current) return;
    const svg = select(svgRef.current);
    const { width: vw, height: vh } = svgRef.current.getBoundingClientRect();
    const availH = vh - LOGO_CLEAR;
    const scale = Math.min(vw / (layout.width + 80), availH / (layout.height + 80), 1);
    const tx = (vw - layout.width * scale) / 2;
    const ty = LOGO_CLEAR + (availH - layout.height * scale) / 2;
    svg.call(zoomBehaviorRef.current.transform, zoomIdentity.translate(tx, ty).scale(scale));
  };

  return (
    <div className="relative h-full w-full">
      <svg ref={svgRef} className="h-full w-full bg-surface" role="img" aria-label="Floor view">
        <g ref={gRef}>
          {layout.entities.map((ent) => (
            <g key={ent.entity.id} transform={`translate(${ent.x},${ent.y})`}>
              {/* Entity rectangle */}
              <rect
                width={ent.w}
                height={ent.h}
                rx={12}
                ry={12}
                className="fill-surface-panel stroke-aurora"
                strokeWidth={1.5}
              />
              {/* Entity label */}
              <text x={ENTITY_PADDING_X} y={36} className="fill-slate-100 text-xl font-semibold">
                {ent.entity.name}
              </text>

              {/* Categories */}
              {ent.categories.map((cat) => (
                <g key={cat.category.id} transform={`translate(${cat.x},${cat.y})`}>
                  <rect
                    width={cat.w}
                    height={cat.h}
                    rx={8}
                    ry={8}
                    className={
                      cat.isSub
                        ? 'fill-surface-panel stroke-aurora'
                        : 'fill-surface-card stroke-aurora-strong'
                    }
                    strokeWidth={1}
                    opacity={cat.isSub ? 0.65 : 1}
                  />
                  <text
                    x={CATEGORY_PADDING}
                    y={22}
                    className={
                      cat.isSub
                        ? 'fill-slate-300 text-sm font-medium'
                        : 'fill-slate-200 text-sm font-semibold uppercase tracking-wider'
                    }
                  >
                    {cat.category.name}
                  </text>

                  {/* Tables */}
                  {cat.tables.map(({ table, cx, cy }) => {
                    const tx = cx - cat.x;
                    const ty = cy - cat.y;
                    const locked = !table.accessible;
                    return (
                      <g
                        key={table.id}
                        transform={`translate(${tx},${ty})`}
                        className="cursor-pointer"
                        opacity={locked ? 0.55 : 1}
                        onClick={() =>
                          locked
                            ? (setLockedModal({ id: table.id, name: table.name }),
                              setRequestState('idle'))
                            : navigate(`/tables/${table.id}`)
                        }
                      >
                        {locked ? (
                          <polygon
                            points={octagonPoints(TABLE_RADIUS)}
                            fill={LOCKED_FILL}
                            stroke={LOCKED_STROKE}
                            strokeWidth={BORDER_WIDTH}
                          />
                        ) : (
                          (() => {
                            const b = borderStroke(table);
                            return (
                              <polygon
                                points={octagonPoints(TABLE_RADIUS)}
                                fill={flagFill(table.status_flag)}
                                stroke={b.color}
                                strokeWidth={b.width}
                              />
                            );
                          })()
                        )}
                        {locked && <LockIcon />}
                        {!locked && (table.status_flag !== 'GREY' || table.is_blue_for_viewer) && (
                          <circle cx={0} cy={0} r={5} fill={borderStroke(table).color} />
                        )}
                        <title>{locked ? `${table.name} — restricted` : table.name}</title>
                        {(() => {
                          const [l1, l2] = wrapTableName(table.name);
                          return (
                            <text textAnchor="middle" className="fill-slate-200 text-[11px]">
                              <tspan x={0} y={TABLE_LABEL_OFFSET}>
                                {l1}
                              </tspan>
                              {l2 && (
                                <tspan x={0} dy={TABLE_LABEL_LINE_H}>
                                  {l2}
                                </tspan>
                              )}
                            </text>
                          );
                        })()}
                      </g>
                    );
                  })}

                  {cat.tables.length === 0 && (
                    <text
                      x={cat.w / 2}
                      y={cat.h / 2 + 4}
                      textAnchor="middle"
                      className="fill-slate-600 text-xs italic"
                    >
                      no tables yet
                    </text>
                  )}
                </g>
              ))}
            </g>
          ))}

          {layout.entities.length === 0 && (
            <text x={400} y={200} className="fill-slate-500 text-base" textAnchor="middle">
              No accessible work efforts. Create one to begin.
            </text>
          )}
        </g>
      </svg>

      <button
        onClick={reset}
        className="absolute left-6 top-6 rounded-md border border-aurora-strong bg-surface-panel px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-700"
      >
        Reset view
      </button>

      <img
        src="/pyek-logo.png"
        alt="Pyek Group"
        className="pointer-events-none absolute left-1/2 top-5 -translate-x-1/2 opacity-70"
        style={{ height: LOGO_H }}
      />

      {/* Locked table modal */}
      {lockedModal && (
        <div
          className="absolute inset-0 z-50 flex items-center justify-center"
          onClick={() => setLockedModal(null)}
        >
          <div className="absolute inset-0 bg-black/50" />
          <div
            className="relative w-80 rounded-xl border border-aurora-strong bg-surface-panel p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-700">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M4 7V5a4 4 0 0 1 8 0v2"
                    stroke="#9CA3AF"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                  />
                  <rect x="2" y="7" width="12" height="9" rx="2" fill="#4B5563" />
                  <circle cx="8" cy="11.5" r="1.5" fill="#9CA3AF" />
                </svg>
              </span>
              <h2 className="text-base font-semibold text-slate-100">{lockedModal.name}</h2>
            </div>
            <p className="text-sm text-slate-400">You don't have access to this work effort.</p>

            {requestState === 'sent' && (
              <p className="mt-3 text-sm font-medium text-pyek-cyan">
                ✓ Access request sent. A team member will review it shortly.
              </p>
            )}
            {requestState === 'duplicate' && (
              <p className="mt-3 text-sm text-slate-400">
                You've already submitted a request for this table.
              </p>
            )}

            <div className="mt-5 flex gap-2">
              {requestState !== 'sent' && (
                <button
                  disabled={requestState === 'pending' || requestState === 'duplicate'}
                  onClick={async () => {
                    setRequestState('pending');
                    try {
                      await api.post(`/api/tables/${lockedModal.id}/access-requests`, {});
                      setRequestState('sent');
                    } catch (err: unknown) {
                      const status = (err as { status?: number })?.status;
                      setRequestState(status === 409 ? 'duplicate' : 'idle');
                    }
                  }}
                  className="flex-1 rounded-lg bg-pyek-cyan/20 py-2 text-sm font-medium text-pyek-cyan transition-colors hover:bg-pyek-cyan/30 disabled:opacity-50"
                >
                  {requestState === 'pending' ? 'Sending…' : 'Request Access'}
                </button>
              )}
              <button
                onClick={() => setLockedModal(null)}
                className="flex-1 rounded-lg border border-aurora bg-slate-800 py-2 text-sm text-slate-300 transition-colors hover:bg-slate-700"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
}

// Split a table name into up to two label lines (max 10 chars each),
// breaking at word boundaries where possible.
function wrapTableName(name: string): [string, string | null] {
  const MAX = 10;
  if (name.length <= MAX) return [name, null];
  const mid = Math.ceil(name.length / 2);
  const before = name.lastIndexOf(' ', mid);
  const after = name.indexOf(' ', mid);
  let breakAt = -1;
  if (before > 0 && after > 0) {
    breakAt = mid - before <= after - mid ? before : after;
  } else if (before > 0) {
    breakAt = before;
  } else if (after > 0) {
    breakAt = after;
  }
  if (breakAt > 0) {
    const l1 = name.slice(0, breakAt);
    const l2 = name.slice(breakAt + 1);
    return [truncate(l1, MAX), truncate(l2, MAX)];
  }
  return [truncate(name, MAX), null];
}
